import React, { useCallback, useEffect, useState } from 'react';
import { getPortions } from '../services/portionService';
import {
  createMobileStandPortion, updateMobileStandPortion, deleteMobileStandPortion,
} from '../services/mobileStandPortionService';

export default function MobileStandPortionModal({ mobileStandId, existing, onClose, onSaved, onManagePortions }) {
  const isEditing = Boolean(existing);

  const [portions, setPortions] = useState([]);
  const [portionId, setPortionId] = useState('');
  const [stock, setStock] = useState(existing ? String(existing.stock ?? 0) : '');
  const [stockMin, setStockMin] = useState(existing ? String(existing.stockMin ?? 0) : '0');
  const [stockOrder, setStockOrder] = useState(existing ? String(existing.stockOrder ?? 0) : '0');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const loadPortions = useCallback(() => {
    getPortions()
      .then(data => setPortions(Array.isArray(data) ? data : []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!isEditing) loadPortions();
  }, [isEditing, loadPortions]);

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    setSaving(true);
    try {
      if (isEditing) {
        await updateMobileStandPortion(existing.id, {
          stock: Number(stock) || 0,
          stockMin: Number(stockMin) || 0,
          stockOrder: Number(stockOrder) || 0,
        });
      } else {
        await createMobileStandPortion({
          portionId: Number(portionId),
          mobileStandId,
          stock: Number(stock) || 0,
          stockMin: Number(stockMin) || 0,
          stockOrder: Number(stockOrder) || 0,
        });
      }
      onSaved();
    } catch (err) {
      setError(err.response?.data?.msg || 'No se pudo guardar la porción.');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    setError('');
    setSaving(true);
    try {
      await deleteMobileStandPortion(existing.id);
      onSaved();
    } catch (err) {
      setError(err.response?.data?.msg || 'No se pudo eliminar la porción.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 300,
        background: 'rgba(0,0,0,0.65)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 16,
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        className="section-card"
        style={{ width: '100%', maxWidth: 480, marginBottom: 0, maxHeight: '90vh', overflowY: 'auto' }}
      >
        <div className="section-header">
          <h6>
            <i className={`bi ${isEditing ? 'bi-pencil-square' : 'bi-plus-circle'}`} style={{ color: 'var(--orig-green)' }} />
            {isEditing ? 'Editar Porción del Stand' : 'Agregar Porción al Inventario'}
          </h6>
          <button
            type="button"
            onClick={onClose}
            style={{ background: 'none', border: 'none', color: 'var(--orig-text-muted)', fontSize: 18, cursor: 'pointer', lineHeight: 1 }}
            title="Cerrar"
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="section-body">
            {error && (
              <div className="alert-orig alert-error" style={{ marginBottom: 16 }}>
                <i className="bi bi-exclamation-triangle-fill" />
                {error}
              </div>
            )}

            <div className="row g-3">
              {isEditing ? (
                <div className="col-12">
                  <div style={{
                    padding: '10px 14px', background: 'var(--orig-panel)',
                    borderRadius: 'var(--orig-radius)', border: '1px solid var(--orig-border)',
                  }}>
                    <div style={{ fontWeight: 600, fontSize: 14, color: 'var(--orig-text)' }}>
                      {existing.portion?.name}
                    </div>
                    {existing.portion?.unitType && (
                      <div style={{ fontSize: 12, color: 'var(--orig-text-muted)' }}>
                        {existing.portion.unitType}
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="col-12">
                  <label className="form-label" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    Porción
                    {onManagePortions && (
                      <button
                        type="button"
                        onClick={onManagePortions}
                        title="Administrar porciones"
                        style={{
                          background: 'var(--orig-green-dim)',
                          border: '1px solid rgba(5,150,105,0.35)',
                          borderRadius: 6, cursor: 'pointer', width: 22, height: 22,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          color: 'var(--orig-green-bright)', fontSize: 12, lineHeight: 1,
                        }}
                      >
                        <i className="bi bi-gear-fill" />
                      </button>
                    )}
                  </label>
                  <select
                    className="form-select"
                    value={portionId}
                    onChange={e => setPortionId(e.target.value)}
                    required
                  >
                    <option value="" disabled>Seleccione una porción</option>
                    {portions.map(p => (
                      <option key={p.id} value={p.id}>
                        {p.name}{p.unitType ? ` — ${p.unitType}` : ''}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div className="col-12 col-md-6">
                <label className="form-label">Stock</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  className="form-control"
                  placeholder="0"
                  value={stock}
                  onChange={e => setStock(e.target.value)}
                  required
                />
              </div>

              <div className="col-12 col-md-6">
                <label className="form-label">Stock mínimo</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  className="form-control"
                  placeholder="0"
                  value={stockMin}
                  onChange={e => setStockMin(e.target.value)}
                />
              </div>
            </div>
          </div>

          <div style={{
            display: 'flex', justifyContent: 'space-between', gap: 10,
            padding: '14px 20px', borderTop: '1px solid var(--orig-border)',
          }}>
            {isEditing ? (
              <button
                type="button"
                className="btn-orig btn-orig-outline"
                onClick={handleDelete}
                disabled={saving}
                style={{ color: 'var(--orig-red-light)' }}
              >
                <i className="bi bi-trash" />
                Eliminar
              </button>
            ) : <span />}
            <div style={{ display: 'flex', gap: 10 }}>
              <button type="button" className="btn-orig btn-orig-outline" onClick={onClose}>
                Cancelar
              </button>
              <button type="submit" className="btn-orig btn-orig-green" disabled={saving}>
                <i className="bi bi-check-lg" />
                {saving ? 'Guardando...' : 'Guardar'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
